import { keepPreviousSearchData } from "@/lib/data/react-query/search/scope";
import { INCIDENT_TYPE_COLUMN_ALIASES } from "@/lib/utils/matters/incident-classification";
import { AddOrEditMatterModal } from "@/components/features/matter/layout/AddOrEditMatterModal/AddOrEditMatterModal";
import { MatterRowContextMenu } from "@/components/features/matters/MatterRowContextMenu";
import MattersSearchBar from "@/components/features/matters/MattersSearchBar/MattersSearchBar";
import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import {
  MY_MATTERS_DASHBOARD_COLUMN_IDS,
  buildMyMattersDashboardColumns,
} from "@/components/features/matters/myMattersDashboardTableColumns";
import { AppModal, AppPageShell, Button } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useSearchObject } from "@/hooks/useSearchObject";
import {
  searchMattersQueryOptions,
  searchMattersCountQueryOptions,
} from "@/lib/data/react-query/matters/search-matters";
import { getSearchMattersCount } from "@/lib/data/requests/matters/search-matters";
import { ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { cn } from "@/lib/utils/cn";
import { mapMyMattersSearchResultToTableRow } from "@/lib/utils/matters/my-matters-search-result-utils";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  functionalUpdate,
  getCoreRowModel,
  useReactTable,
  type SortingFn,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { SlidersHorizontal, X, Plus } from "lucide-react";
import pluralize from "pluralize";

/** Must stay in sync with `tanstackTableNames.myMattersDashboardTab`. */
const MY_MATTERS_DASHBOARD_TABLE_LS_KEY: string =
  tanstackTableNames.myMattersDashboardTab;

const normalizeMyMattersDashboardColumnId = (columnId: string) => {
  if (columnId === "Date of Incident") return "Date of Loss";
  if (columnId === "Description") return "Incident Description";
  if (columnId === "Incident State") return "Incident Address";
  return INCIDENT_TYPE_COLUMN_ALIASES[columnId] ?? columnId;
};

const MATTER_SEARCH_SORT_FIELDS: Record<string, string> = {
  Assignees: "assigneeName",
  Client: "clientName",
  "Case Status": "status",
  "Incident Type": "incidentType",
  "Date Created": "dateCreated",
  "Date Modified": "dateModified",
  "Date of Loss": "dateOfLoss",
  "Incident Description": "incidentDescription",
  "Injury Description": "clientInjuryDescription",
  "Incident Address": "incidentAddress",
  "Medical Treatment Bill Total": "medicalTreatmentBillTotal",
  "SOL Date": "statuteOfLimitations",
  "Total Costs": "expenseTotal",
  Title: "title",
};

type MyMattersDashboardTabViewProps = {
  topContent?: ReactNode;
};

export function MyMattersDashboardTabView({
  topContent,
}: MyMattersDashboardTabViewProps) {
  const { currentUser, hasActionPermission } = useUser();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);
  const [draftSearchObject, setDraftSearchObject] = useState<{
    [key: string]: string[];
  }>({});
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const {
    searchObject: matterFilterSearchObject,
    addSearchObjectValue,
    removeSearchObjectValue,
    clearSearchObject,
    replaceSearchObject,
  } = useSearchObject({
    syncWithSearchParams: true,
    itemType: "myMatters",
    persistSearchObject: true,
  });
  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(MY_MATTERS_DASHBOARD_TABLE_LS_KEY);
    return (stored.sorting ?? []).map((sort) => ({
      ...sort,
      id: normalizeMyMattersDashboardColumnId(sort.id),
    }));
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(MY_MATTERS_DASHBOARD_TABLE_LS_KEY);
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 25,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(MY_MATTERS_DASHBOARD_TABLE_LS_KEY);
    const ids = [...MY_MATTERS_DASHBOARD_COLUMN_IDS];
    const order = stored.columnOrder
      ? [
          ...new Set(
            stored.columnOrder.map(normalizeMyMattersDashboardColumnId),
          ),
        ]
      : undefined;
    if (order?.length) {
      return [...order, ...ids.filter((id) => !order.includes(id))];
    }
    return ids;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(MY_MATTERS_DASHBOARD_TABLE_LS_KEY);
      const storedVisibility = Object.fromEntries(
        Object.entries(stored.columnVisibility ?? {}).map(([id, visible]) => [
          normalizeMyMattersDashboardColumnId(id),
          visible,
        ]),
      );
      return {
        ...storedVisibility,
        Title: true,
      };
    },
  );

  const sortInput = useMemo(() => {
    const activeSort = sorting.at(0);
    const sort = activeSort && MATTER_SEARCH_SORT_FIELDS[activeSort.id];
    return sort
      ? { order: activeSort.desc ? ("desc" as const) : ("asc" as const), sort }
      : {};
  }, [sorting]);
  const activeFilterCount = Object.values(matterFilterSearchObject).reduce(
    (count, values) => count + values.length,
    0,
  );
  const { data: draftMattersData, isFetching: isDraftMattersFetching } =
    useQuery({
      ...searchMattersCountQueryOptions({
        ...draftSearchObject,
        userId: currentUser.id,
        search: debouncedSearchQuery || undefined,
        statusCategories: [...ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS],
      }),
      enabled: isMoreFiltersOpen,
    });
  const draftMatterCount = getSearchMattersCount(draftMattersData);
  const addDraftSearchObjectValue = (
    key: string,
    value: string,
    replace = false,
  ) => {
    setDraftSearchObject((current) => {
      if (replace) return { ...current, [key]: [value] };
      const values = current[key] ?? [];
      return values.includes(value)
        ? current
        : { ...current, [key]: [...values, value] };
    });
  };
  const removeDraftSearchObjectValue = (key: string, value: string) => {
    setDraftSearchObject((current) => {
      const values = (current[key] ?? []).filter((item) => item !== value);
      if (values.length > 0) return { ...current, [key]: values };
      return Object.fromEntries(
        Object.entries(current).filter(([currentKey]) => currentKey !== key),
      );
    });
  };
  const openMoreFilters = () => {
    setDraftSearchObject(matterFilterSearchObject);
    setIsMoreFiltersOpen(true);
  };
  const { data, isFetching, isLoading, isError } = useQuery({
    ...searchMattersQueryOptions({
      ...matterFilterSearchObject,
      userId: currentUser.id,
      includeRelatedMatters: true,
      search: debouncedSearchQuery || undefined,
      statusCategories: [...ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS],
      pageNumber: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...sortInput,
    }),
    placeholderData: keepPreviousSearchData,
  });

  const rows = useMemo(
    () => (data?.data ?? []).map(mapMyMattersSearchResultToTableRow),
    [data?.data],
  );
  const mattersById = useMemo(
    () => new Map((data?.data ?? []).map((matter) => [matter.id, matter])),
    [data?.data],
  );
  useEffect(() => {
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [debouncedSearchQuery, matterFilterSearchObject]);
  const totalRowCount = getSearchMattersCount(data) ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalRowCount / pagination.pageSize));

  const columns = useMemo(() => buildMyMattersDashboardColumns(), []);

  const table = useReactTable({
    data: rows,
    columns,
    manualPagination: true,
    manualSorting: true,
    pageCount,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MY_MATTERS_DASHBOARD_TABLE_LS_KEY, {
          sorting: next,
        });
        return next;
      });
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MY_MATTERS_DASHBOARD_TABLE_LS_KEY, {
          pagination: { pageSize: next.pageSize },
        });
        return next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MY_MATTERS_DASHBOARD_TABLE_LS_KEY, {
          columnOrder: next,
        });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Title: true };
        persistTanstackTablePrefs(MY_MATTERS_DASHBOARD_TABLE_LS_KEY, {
          columnVisibility: merged,
        });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    sortingFns: {
      customDateTime: (() => 0) as SortingFn<unknown>,
    },
  });

  const sensors = useMattersStyleTableColumnDragSensors();
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.create,
  );

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(MY_MATTERS_DASHBOARD_TABLE_LS_KEY, {
        columnOrder: next,
      });
      return next;
    });
  };

  return (
    <AppPageShell className="h-full min-h-0 overflow-hidden">
      {topContent}
      <MattersStyleTableShell
        scrollableBody
        table={table}
        columnOrder={columnOrder}
        columnIdCountBaseline={MY_MATTERS_DASHBOARD_COLUMN_IDS.length}
        onColumnDragEnd={handleColumnDragEnd}
        sensors={sensors}
        fixedColumnWidths
        search={{
          maxLength: 200,
          value: searchQuery,
          onChange: (value) => {
            setSearchQuery(value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          },
          placeholder: `Search ${pluralize("matter", totalRowCount, true)}...`,
        }}
        searchActions={
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <MattersSearchBar
              searchObject={matterFilterSearchObject}
              addSearchObjectValue={addSearchObjectValue}
              removeSearchObjectValue={removeSearchObjectValue}
              showAssigneeFilter={false}
              statusCategories={[...ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS]}
              layout="quick"
            />
            <div
              className={cn(
                "relative inline-flex",
                activeFilterCount > 0 &&
                  "h-8 overflow-hidden rounded-full border",
              )}
            >
              <Button
                type="button"
                variant={activeFilterCount > 0 ? "ghost" : "outline"}
                aria-label="All filters"
                title="All filters"
                className={cn(
                  "h-8 px-3 text-xs font-medium",
                  activeFilterCount > 0 ? "rounded-none" : "rounded-full",
                )}
                onClick={() => {
                  openMoreFilters();
                }}
              >
                <SlidersHorizontal />
                All filters
                {activeFilterCount > 0 ? (
                  <span className="bg-destructive text-destructive-foreground min-w-5 rounded-full px-1 py-0.5 text-center text-xs leading-none">
                    {activeFilterCount}
                  </span>
                ) : null}
              </Button>
              {activeFilterCount > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  aria-label="Clear filters"
                  title="Clear filters"
                  className="h-8 w-8 rounded-none border-l p-0"
                  onClick={() => {
                    void clearSearchObject();
                  }}
                >
                  <X />
                </Button>
              ) : null}
            </div>
          </div>
        }
        toolbarTrailing={
          hasCreatePermission ? (
            <Button
              variant="foreground"
              size="md"
              onClick={() => {
                setIsCreating(true);
              }}
              data-testid="create-matter-button"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Create matter
            </Button>
          ) : null
        }
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        errorMessage="Unable to load matters."
        emptyMessage="No matters found for this view."
        totalFilteredRowCount={totalRowCount}
        entityNamePlural="matters"
        pageSizeOptions={[10, 25, 50, 100]}
        sorting={sorting}
        getTableRowProps={(row) => ({
          className: "cursor-pointer",
          onClick: () => {
            void navigate({
              to: "/matters/$matterId",
              params: { matterId: row.original.matterId },
            });
          },
        })}
        renderContextMenu={(row, trigger) => {
          const matter = mattersById.get(row.original.matterId);
          return matter ? (
            <MatterRowContextMenu
              allowedStatusCategories={ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS}
              matter={matter}
              trigger={trigger}
            />
          ) : (
            trigger
          );
        }}
      />

      <AppModal
        open={isMoreFiltersOpen}
        onOpenChange={setIsMoreFiltersOpen}
        title="All filters"
        description="Choose filters, then apply them to the matters table."
        size="lg"
        bodyContentClassName="p-0"
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <Button
              type="button"
              variant="link"
              onClick={() => {
                setDraftSearchObject({});
              }}
            >
              Clear all
            </Button>
            <Button
              type="button"
              variant="foreground"
              disabled={draftMatterCount === undefined}
              onClick={() => {
                void (Object.keys(draftSearchObject).length
                  ? replaceSearchObject(draftSearchObject)
                  : clearSearchObject());
                setIsMoreFiltersOpen(false);
              }}
            >
              {isDraftMattersFetching || draftMatterCount === undefined
                ? "Loading results..."
                : `Show ${draftMatterCount} results`}
            </Button>
          </div>
        }
      >
        <div className="p-4">
          <MattersSearchBar
            searchObject={draftSearchObject}
            addSearchObjectValue={addDraftSearchObjectValue}
            removeSearchObjectValue={removeDraftSearchObjectValue}
            showAssigneeFilter={false}
            statusCategories={[...ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS]}
            layout="list"
          />
        </div>
      </AppModal>

      <AddOrEditMatterModal
        open={isCreating}
        setOpen={setIsCreating}
        editing={false}
        creatingMore={false}
        allowedMatterStatusCategories={[
          ...ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS,
        ]}
        afterCreateFn={(res) => navigate({ to: `/matters/${res.data.id}` })}
      />
    </AppPageShell>
  );
}
